/**
 * Quotes page
 * Main page for managing quotes/estimates
 */

import { useState, useEffect } from "react";
import { useServiceFlow } from "@/contexts";
import {
  Icon,
  QuoteModal,
  ConfirmModal,
  AssignTechnicianModal,
} from "@/components/ui";
import { generateQuotePDF } from "@/utils/pdfGenerator";
import styles from "./QuotesPage.module.css";

interface Quote {
  id: string;
  quoteNumber: string;
  clientName: string;
  clientId: string;
  date: string;
  validUntil: string;
  total: number;
  subtotal?: number;
  tax?: number;
  status: "draft" | "sent" | "approved" | "rejected" | "expired";
  services: string[];
  lineItems?: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
  notes?: string;
  token?: string; // Token único para acceso público
  sentAt?: string; // Fecha de envío por email
  pestType?:
    | "Cucarachas"
    | "Ratas"
    | "Ratones"
    | "Hormigas"
    | "Chinches"
    | "Termitas"
    | "Moscas"
    | "Mosquitos"
    | "Avispas"
    | "Pulgas"
    | "Garrapatas"
    | "Otros"; // Tipo de plaga a controlar
  rejectionReason?: string; // Motivo del rechazo si fue rechazado
}

const mockQuotes: Quote[] = [
  {
    id: "1",
    quoteNumber: "PRE-2026-001",
    clientName: "Hotel Míramar",
    clientId: "1",
    date: "2026-01-15",
    validUntil: "2026-02-28",
    total: 1250.0,
    subtotal: 1033.06,
    tax: 216.94,
    status: "sent",
    services: ["Control de Plagas", "Desinfección"],
    lineItems: [
      {
        id: "1",
        description: "Control de Plagas",
        quantity: 1,
        unitPrice: 600,
        total: 600,
      },
      {
        id: "2",
        description: "Desinfección",
        quantity: 1,
        unitPrice: 450,
        total: 450,
      },
    ],
    notes: "Servicio mensual con revisión completa",
    token: "qt_9f8e7d6c5b4a3210",
    sentAt: "2026-01-15T10:30:00Z",
    pestType: "Cucarachas",
  },
  {
    id: "2",
    quoteNumber: "PRE-2026-002",
    clientName: "Restaurante El Olivo",
    clientId: "2",
    date: "2026-01-10",
    validUntil: "2026-03-10",
    total: 850.0,
    subtotal: 702.48,
    tax: 147.52,
    status: "approved",
    services: ["Desratización", "Fumigación"],
    lineItems: [
      {
        id: "1",
        description: "Desratización",
        quantity: 1,
        unitPrice: 400,
        total: 400,
      },
      {
        id: "2",
        description: "Fumigación",
        quantity: 1,
        unitPrice: 300,
        total: 300,
      },
    ],
    token: "qt_1a2b3c4d5e6f7890",
    sentAt: "2026-01-10T09:15:00Z",
    pestType: "Ratas",
  },
  {
    id: "3",
    quoteNumber: "PRE-2026-003",
    clientName: "Clínica San José",
    clientId: "3",
    date: "2026-01-05",
    validUntil: "2026-02-28",
    total: 2100.0,
    subtotal: 1735.54,
    tax: 364.46,
    status: "draft",
    services: ["Control DDD", "Inspección"],
    lineItems: [
      {
        id: "1",
        description: "Control DDD Completo",
        quantity: 1,
        unitPrice: 1200,
        total: 1200,
      },
      {
        id: "2",
        description: "Inspección Sanitaria",
        quantity: 1,
        unitPrice: 550,
        total: 550,
      },
    ],
    pestType: "Chinches",
    // Sin token porque aún es borrador
  },
  {
    id: "4",
    quoteNumber: "PRE-2025-098",
    clientName: "Supermercados Del Norte",
    clientId: "4",
    date: "2025-12-01",
    validUntil: "2026-01-01",
    total: 1500.0,
    subtotal: 1239.67,
    tax: 260.33,
    status: "expired",
    services: ["Control Integral"],
    lineItems: [
      {
        id: "1",
        description: "Control Integral de Plagas",
        quantity: 1,
        unitPrice: 1250,
        total: 1250,
      },
    ],
    token: "qt_fed0cba9876543210",
    sentAt: "2025-12-01T14:45:00Z",
    pestType: "Termitas",
  },
  {
    id: "5",
    quoteNumber: "PRE-2026-004",
    clientName: "Oficinas Empresariales S.L.",
    clientId: "5",
    date: "2026-01-08",
    validUntil: "2026-02-28",
    total: 975.0,
    subtotal: 805.79,
    tax: 169.21,
    status: "rejected",
    services: ["Control de Hormigas"],
    lineItems: [
      {
        id: "1",
        description: "Tratamiento de Hormigas",
        quantity: 1,
        unitPrice: 550,
        total: 550,
      },
      {
        id: "2",
        description: "Revisión y Seguimiento",
        quantity: 1,
        unitPrice: 255,
        total: 255,
      },
    ],
    notes: "Presupuesto rechazado por el cliente",
    token: "qt_abc123def456ghi7",
    sentAt: "2026-01-08T11:20:00Z",
    pestType: "Hormigas",
    rejectionReason:
      "El presupuesto supera nuestro presupuesto anual. Necesitamos una opción más económica.",
  },
];

const getStatusLabel = (status: Quote["status"]): string => {
  const labels = {
    draft: "Borrador",
    sent: "Enviado",
    approved: "Aprobado",
    rejected: "Rechazado",
    expired: "Expirado",
  };
  return labels[status];
};

const getStatusColor = (status: Quote["status"]): string => {
  const colors = {
    draft: "#6b7280",
    sent: "#3b82f6",
    approved: "#10b981",
    rejected: "#ef4444",
    expired: "#f59e0b",
  };
  return colors[status];
};

const formatDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
};

/**
 * Quotes page component
 */
export function QuotesPage() {
  const {
    pendingQuote,
    setPendingQuote,
    createWorkOrderFromQuote,
    setPendingWorkOrder,
  } = useServiceFlow();
  const [quotes, setQuotes] = useState<Quote[]>(mockQuotes);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(
    mockQuotes[0],
  );
  const [statusFilter, setStatusFilter] = useState("Todos");
  const [searchQuery, setSearchQuery] = useState("");
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [editingQuote, setEditingQuote] = useState<Quote | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [quoteToConvert, setQuoteToConvert] = useState<Quote | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: "info" | "warning" | "danger" | "success";
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    type: "info",
    onConfirm: () => {},
  });

  // Detect pending quote from service request
  useEffect(() => {
    if (pendingQuote) {
      // Check if quote already exists
      const exists = quotes.some((q) => q.quoteNumber === pendingQuote.id);
      if (exists) {
        setPendingQuote(null);
        return;
      }

      // Convert to QuotesPage format with proper structure
      const subtotal = pendingQuote.amount || 0;
      const tax = subtotal * 0.21;
      const total = subtotal + tax;

      const newQuote: Quote = {
        id: pendingQuote.id,
        quoteNumber: pendingQuote.id,
        clientName: pendingQuote.clientName,
        clientId: pendingQuote.clientId,
        date: new Date().toISOString().split("T")[0],
        validUntil: pendingQuote.validUntil.toISOString().split("T")[0],
        subtotal: subtotal,
        tax: tax,
        total: total,
        status: "draft",
        services: [pendingQuote.serviceType],
        pestType: pendingQuote.serviceType as any,
        notes: `Solicitud: ${pendingQuote.requestId}\n\n${pendingQuote.description}`,
        lineItems: [
          {
            id: "1",
            description: pendingQuote.serviceType,
            quantity: 1,
            unitPrice: subtotal,
            total: subtotal,
          },
        ],
      };

      // Add to quotes list
      setQuotes((prev) => [newQuote, ...prev]);
      setSelectedQuote(newQuote);
      setEditingQuote(newQuote);
      setShowQuoteModal(true);

      // Clear pending quote
      setPendingQuote(null);
    }
  }, [pendingQuote, setPendingQuote, quotes]);

  const filteredQuotes = quotes.filter((quote) => {
    const matchesSearch =
      searchQuery === "" ||
      quote.quoteNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
      quote.clientName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus =
      statusFilter === "Todos" || getStatusLabel(quote.status) === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const handleDownloadPDF = (quote: Quote) => {
    // Preparar datos para el PDF
    const pdfData = {
      quoteNumber: quote.quoteNumber,
      clientName: quote.clientName,
      date: quote.date,
      validUntil: quote.validUntil,
      lineItems: quote.lineItems || [],
      subtotal: quote.subtotal || 0,
      tax: quote.tax || 0,
      total: quote.total,
      notes: quote.notes,
      pestType: quote.pestType,
    };

    // Generar y descargar PDF
    // @ts-expect-error: LineItem type mismatch - discount property needs refactoring
    generateQuotePDF(pdfData);
  };

  const handleDeleteQuote = (quoteId: string) => {
    const quoteToDelete = quotes.find((q) => q.id === quoteId);
    setConfirmModal({
      isOpen: true,
      title: "Eliminar Presupuesto",
      message: `¿Estás seguro de que deseas eliminar el presupuesto ${quoteToDelete?.quoteNumber}? Esta acción no se puede deshacer.`,
      type: "danger",
      onConfirm: () => {
        setQuotes((prevQuotes) => prevQuotes.filter((q) => q.id !== quoteId));
        if (selectedQuote?.id === quoteId) {
          setSelectedQuote(null);
        }
      },
    });
  };

  const handleEditQuote = (quote: Quote) => {
    setEditingQuote(quote);
    setShowQuoteModal(true);
  };

  const handleCreateQuote = () => {
    setEditingQuote(null);
    setShowQuoteModal(true);
  };

  const handleDuplicateQuote = (quote: Quote) => {
    const newQuote: Quote = {
      ...quote,
      id: String(Date.now()),
      quoteNumber: `PRE-${new Date().getFullYear()}-${String(quotes.length + 1).padStart(3, "0")}`,
      status: "draft",
      date: new Date().toISOString().split("T")[0],
      validUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0],
      token: undefined, // Los borradores no tienen token hasta que se envían
      sentAt: undefined,
    };
    setQuotes([...quotes, newQuote]);
    setSelectedQuote(newQuote);
  };

  const handleSendByEmail = (quoteId: string) => {
    const quote = quotes.find((q) => q.id === quoteId);
    if (!quote) return;

    // Generar token si no existe
    const token =
      quote.token || `qt_${Math.random().toString(36).substring(2, 18)}`;
    const publicUrl = `${window.location.origin}/public/quote?token=${token}`;

    setConfirmModal({
      isOpen: true,
      title: "Enviar Presupuesto por Email",
      message: `Se enviará el presupuesto ${quote.quoteNumber} al cliente ${quote.clientName}. Se generará un enlace público para que el cliente pueda ver y aceptar/rechazar el presupuesto.\n\nEnlace público:\n${publicUrl}`,
      type: "info",
      onConfirm: () => {
        // Actualizar el presupuesto con el token y cambiar estado a "sent"
        setQuotes((prevQuotes) =>
          prevQuotes.map((q) =>
            q.id === quoteId
              ? {
                  ...q,
                  status: "sent",
                  token,
                  sentAt: new Date().toISOString(),
                }
              : q,
          ),
        );

        // En producción, aquí se enviaría el email con el enlace
        // TODO: Implementar envío real de email cuando el backend esté disponible

        // Mostrar confirmación de éxito
        setTimeout(() => {
          setConfirmModal({
            isOpen: true,
            title: "Email Enviado",
            message: `El presupuesto ha sido enviado exitosamente a ${quote.clientName}. El cliente podrá ver y responder al presupuesto desde el enlace enviado por email.`,
            type: "success",
            onConfirm: () => {},
          });
        }, 500);
      },
    });
  };

  const handleSaveQuote = (quoteData: Quote) => {
    if (editingQuote) {
      // Editar presupuesto existente
      setQuotes((prevQuotes) =>
        prevQuotes.map((q) =>
          q.id === editingQuote.id ? { ...q, ...quoteData } : q,
        ),
      );
      if (selectedQuote?.id === editingQuote.id) {
        setSelectedQuote((prev) => (prev ? { ...prev, ...quoteData } : null));
      }
    } else {
      // Crear nuevo presupuesto
      const newQuote: Quote = {
        ...quoteData,
        id: String(Date.now()),
        quoteNumber: `PRE-2024-${String(quotes.length + 1).padStart(3, "0")}`,
        status: "draft",
      };
      setQuotes((prevQuotes) => [newQuote, ...prevQuotes]);
      setSelectedQuote(newQuote);
    }
    setShowQuoteModal(false);
    setEditingQuote(null);
  };

  const handleChangeStatus = (quoteId: string, newStatus: Quote["status"]) => {
    setQuotes((prevQuotes) =>
      prevQuotes.map((q) =>
        q.id === quoteId ? { ...q, status: newStatus } : q,
      ),
    );
    if (selectedQuote?.id === quoteId) {
      setSelectedQuote((prev) =>
        prev ? { ...prev, status: newStatus } : null,
      );
    }
  };

  const handleConvertToWorkOrder = (quote: Quote) => {
    setQuoteToConvert(quote);
    setShowAssignModal(true);
  };

  const handleAssignTechnician = (
    technicianId: string,
    scheduledDate: string,
  ) => {
    if (!quoteToConvert) return;

    // TODO: Aquí se crearía la orden de trabajo con el técnico y fecha
    console.log("Crear orden:", {
      quoteId: quoteToConvert.id,
      technicianId,
      scheduledDate,
    });

    // Marcar presupuesto como aprobado
    handleChangeStatus(quoteToConvert.id, "approved");

    setShowAssignModal(false);
    setQuoteToConvert(null);

    setConfirmModal({
      isOpen: true,
      title: "¡Orden Creada!",
      message: `La orden de trabajo ha sido creada exitosamente y asignada al técnico para el ${new Date(scheduledDate).toLocaleDateString("es-ES")}.`,
      type: "success",
      onConfirm: () => {
        setConfirmModal({ ...confirmModal, isOpen: false });
      },
    });
  };

  // Mock de clientes para el modal
  const mockClients = [
    { id: "1", name: "Hotel Míramar" },
    { id: "2", name: "Restaurante El Olivo" },
    { id: "3", name: "Clínica San José" },
    { id: "4", name: "Supermercados Del Norte" },
  ];

  return (
    <div className={styles.quotesPage}>
      {/* Main Content */}
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2 className={styles.title}>Gestión de Presupuestos</h2>
            <div className={styles.breadcrumb}>
              <span>Inicio</span>
              <span className={styles.breadcrumbSeparator}>›</span>
              <span className={styles.breadcrumbCurrent}>Presupuestos</span>
            </div>
          </div>
          <div className={styles.headerRight}>
            <button className={styles.filterButton}>
              <Icon name="filter" size={16} />
              Limpiar filtros
            </button>
            <button
              className={styles.newQuoteButton}
              onClick={handleCreateQuote}
            >
              <Icon name="plus" size={18} />
              Nuevo Presupuesto
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className={styles.filters}>
          <div className={styles.filterGroup}>
            <label className={styles.filterLabel}>Estado:</label>
            <select
              className={styles.select}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option>Todos</option>
              <option>Borrador</option>
              <option>Enviado</option>
              <option>Aprobado</option>
              <option>Rechazado</option>
              <option>Expirado</option>
            </select>
          </div>

          <div className={styles.searchBox}>
            <span className={styles.searchIcon}>
              <Icon name="search" size={16} />
            </span>
            <input
              type="text"
              className={styles.searchInput}
              placeholder="Buscar presupuesto..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {/* Table */}
        <div className={styles.tableContainer}>
          {filteredQuotes.length > 0 ? (
            <table className={styles.table}>
              <thead className={styles.tableHeader}>
                <tr>
                  <th>N° PRESUPUESTO</th>
                  <th>CLIENTE</th>
                  <th>FECHA</th>
                  <th>VÁLIDO HASTA</th>
                  <th>IMPORTE</th>
                  <th>ESTADO</th>
                  <th>ACCIONES</th>
                </tr>
              </thead>
              <tbody className={styles.tableBody}>
                {filteredQuotes.map((quote) => (
                  <tr
                    key={quote.id}
                    className={
                      selectedQuote?.id === quote.id ? styles.selected : ""
                    }
                    onClick={() => setSelectedQuote(quote)}
                  >
                    <td>
                      <span className={styles.quoteNumber}>
                        {quote.quoteNumber}
                      </span>
                    </td>
                    <td>
                      <span className={styles.clientName}>
                        {quote.clientName}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {formatDate(quote.date)}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: "13px", color: "#6b7280" }}>
                        {formatDate(quote.validUntil)}
                      </span>
                    </td>
                    <td>
                      <span className={styles.amount}>
                        {quote.total.toFixed(2)} €
                      </span>
                    </td>
                    <td>
                      <span
                        className={styles.statusBadge}
                        style={{
                          backgroundColor: `${getStatusColor(quote.status)}20`,
                          color: getStatusColor(quote.status),
                        }}
                      >
                        {getStatusLabel(quote.status)}
                      </span>
                    </td>
                    <td>
                      <div className={styles.actionButtons}>
                        <button
                          className={styles.editButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditQuote(quote);
                          }}
                          title="Editar"
                        >
                          <Icon name="edit" size={16} />
                        </button>
                        <button
                          className={styles.duplicateButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateQuote(quote);
                          }}
                          title="Duplicar"
                        >
                          <Icon name="document" size={16} />
                        </button>
                        <button
                          className={styles.deleteButton}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteQuote(quote.id);
                          }}
                          title="Eliminar"
                        >
                          <Icon name="xCircle" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.emptyState}>
              <Icon name="document" size={48} />
              <p>No se encontraron presupuestos</p>
            </div>
          )}
        </div>
      </div>

      {/* Sidebar */}
      {selectedQuote && (
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <div className={styles.sidebarQuote}>
              <div className={styles.sidebarQuoteInfo}>
                <h3>{selectedQuote.quoteNumber}</h3>
                <p className={styles.sidebarClientName}>
                  {selectedQuote.clientName}
                </p>
              </div>
            </div>
            <div className={styles.headerActions}>
              <button
                className={styles.downloadButton}
                title="Descargar PDF"
                onClick={() => handleDownloadPDF(selectedQuote)}
              >
                <Icon name="download" size={18} />
              </button>
              <button className={styles.moreButton} title="Más opciones">
                <Icon name="moreVertical" size={18} />
              </button>
            </div>
          </div>

          <div className={styles.sidebarContent}>
            {/* Status */}
            <div className={styles.sidebarSection}>
              <div className={styles.statusCard}>
                <div className={styles.statusCardHeader}>
                  <span>ESTADO</span>
                  <span
                    className={styles.statusBadgeLarge}
                    style={{
                      backgroundColor: `${getStatusColor(selectedQuote.status)}20`,
                      color: getStatusColor(selectedQuote.status),
                    }}
                  >
                    {getStatusLabel(selectedQuote.status)}
                  </span>
                </div>
                <div className={styles.statusCardInfo}>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Fecha:</span>
                    <span className={styles.infoValue}>
                      {formatDate(selectedQuote.date)}
                    </span>
                  </div>
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Válido hasta:</span>
                    <span className={styles.infoValue}>
                      {formatDate(selectedQuote.validUntil)}
                    </span>
                  </div>
                  {selectedQuote.pestType && (
                    <div className={styles.infoRow}>
                      <span className={styles.infoLabel}>Tipo de plaga:</span>
                      <span
                        className={styles.infoValue}
                        style={{
                          color: "#059669",
                          fontWeight: "600",
                          backgroundColor: "#d1fae5",
                          padding: "0.25rem 0.5rem",
                          borderRadius: "4px",
                          fontSize: "13px",
                        }}
                      >
                        {selectedQuote.pestType}
                      </span>
                    </div>
                  )}
                  <div className={styles.infoRow}>
                    <span className={styles.infoLabel}>Importe:</span>
                    <span className={styles.infoValueBold}>
                      {selectedQuote.total.toFixed(2)} €
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Services */}
            <div className={styles.sidebarSection}>
              <h4>SERVICIOS INCLUIDOS</h4>
              <div className={styles.servicesList}>
                {selectedQuote.services && selectedQuote.services.length > 0 ? (
                  selectedQuote.services.map((service, index) => (
                    <div key={index} className={styles.serviceItem}>
                      <Icon
                        name="checkCircle"
                        size={16}
                        className={styles.serviceIcon}
                      />
                      <span>{service}</span>
                    </div>
                  ))
                ) : (
                  <p style={{ color: "#6b7280", fontSize: "14px" }}>
                    No hay servicios especificados
                  </p>
                )}
              </div>
            </div>

            {/* Rejection Reason */}
            {selectedQuote.status === "rejected" &&
              selectedQuote.rejectionReason && (
                <div className={styles.sidebarSection}>
                  <h4 style={{ color: "#ef4444" }}>MOTIVO DEL RECHAZO</h4>
                  <div
                    style={{
                      backgroundColor: "#fef2f2",
                      border: "1px solid #fecaca",
                      borderLeft: "4px solid #ef4444",
                      padding: "1rem",
                      borderRadius: "6px",
                      color: "#7f1d1d",
                      fontSize: "14px",
                      lineHeight: "1.5",
                    }}
                  >
                    {selectedQuote.rejectionReason}
                  </div>
                </div>
              )}

            {/* Actions */}
            <div className={styles.sidebarActions}>
              {selectedQuote.status === "draft" && (
                <button
                  className={styles.sendButton}
                  onClick={() => handleSendByEmail(selectedQuote.id)}
                >
                  <Icon name="mail" size={18} />
                  Enviar por Email
                </button>
              )}
              {selectedQuote.status === "sent" && (
                <>
                  <button
                    className={styles.resendButton}
                    onClick={() => handleSendByEmail(selectedQuote.id)}
                  >
                    <Icon name="mail" size={18} />
                    Reenviar Email
                  </button>
                  <button
                    className={styles.approveButton}
                    onClick={() =>
                      handleChangeStatus(selectedQuote.id, "approved")
                    }
                  >
                    <Icon name="checkCircle" size={18} />
                    Marcar como Aprobado
                  </button>
                  <button
                    className={styles.rejectButton}
                    onClick={() =>
                      handleChangeStatus(selectedQuote.id, "rejected")
                    }
                  >
                    <Icon name="xCircle" size={18} />
                    Marcar como Rechazado
                  </button>
                </>
              )}
              {selectedQuote.status === "approved" && (
                <button
                  className={styles.convertButton}
                  onClick={() => handleConvertToWorkOrder(selectedQuote)}
                >
                  <Icon name="clipboard" size={18} />
                  Convertir en Orden de Trabajo
                </button>
              )}
            </div>
          </div>
        </aside>
      )}

      {/* Quote Modal */}
      {/* Quote Modal */}
      <QuoteModal
        isOpen={showQuoteModal}
        // @ts-ignore - Quote type incompatibility needs full type refactor
        quote={editingQuote}
        clients={mockClients}
        onClose={() => {
          setShowQuoteModal(false);
          setEditingQuote(null);
        }}
        // @ts-ignore - Quote type incompatibility needs full type refactor
        onSave={handleSaveQuote}
      />

      {/* Assign Technician Modal */}
      <AssignTechnicianModal
        isOpen={showAssignModal}
        onClose={() => {
          setShowAssignModal(false);
          setQuoteToConvert(null);
        }}
        onAssign={handleAssignTechnician}
        technicians={[
          { id: "1", name: "Juan Pérez", initials: "JP" },
          { id: "2", name: "María García", initials: "MG" },
          { id: "3", name: "Carlos Rodríguez", initials: "CR" },
        ]}
        quoteName={quoteToConvert?.quoteNumber}
      />

      {/* Confirmation Modal */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.type === "danger" ? "Eliminar" : "Confirmar"}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ ...confirmModal, isOpen: false })}
      />
    </div>
  );
}

export default QuotesPage;
